/**
 * WavFilePlayer Component
 * 
 * Generic reusable component that accepts wavFileUrl prop and uses WaveSurfer.js
 * for waveform visualization and built-in player controls.
 */

import React, { useRef, useCallback } from 'react';
import { useWavesurfer } from '@wavesurfer/react';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import Icon from "@amzn/meridian/icon";
import playTokens from "@amzn/meridian-tokens/base/icon/play";
import pauseTokens from "@amzn/meridian-tokens/base/icon/pause";

export interface WavFilePlayerProps {
    /** WAV file URL to load and play */
    wavFileUrl: string;
    /** Optional error callback */
    onError?: (error: Error) => void;
    /** Optional ready callback */
    onReady?: () => void;
}

/**
 * Helper function to format time in MM:SS format
 */
const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * WavFilePlayer component that uses WaveSurfer.js for waveform visualization
 * and built-in audio playback controls. Uses only Meridian components for
 * layout and styling.
 */
export const WavFilePlayer: React.FC<WavFilePlayerProps> = ({
    wavFileUrl,
    onError,
    onReady
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // No URL state
    if (!wavFileUrl) {
        return (
            <Column spacing="200">
                <Text type="b300">Audio Player</Text>
                <Alert type="neutral">
                    <Text type="b200">No audio file available</Text>
                </Alert>
            </Column>
        );
    }

    // WaveSurfer configuration optimized for librosa-style scientific visualization
    const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
        container: containerRef,
        height: 140,
        waveColor: '#2196F3',   
        progressColor: '#1565C0',   // Darker analytical blue for progress
        cursorColor: '#0D47A1',  
        barWidth: 1,                // Thin bars for continuous appearance
        barGap: 0,                  // No gaps for continuous waveform
        barRadius: 0,               // No rounded bars for analytical look
        normalize: true,            // Keep consistent amplitude scaling
        fillParent: true,           // Smooth continuous waveform
        backend: 'WebAudio',        // Better audio procssing
        url: wavFileUrl,
        sampleRate: 44100,
        minPxPerSec: 1,
    });

    // Handle play/pause button click
    const onPlayPause = useCallback(() => {
        if (wavesurfer) {
            wavesurfer.playPause();
        }
    }, [wavesurfer]);

    // Handle wavesurfer ready event
    React.useEffect(() => {
        if (wavesurfer) {
            const handleReady = () => {
                onReady?.();
            };

            const handleError = (err: any) => {
                const errorMessage = err instanceof Error ? err.message : 'Failed to load audio file';
                onError?.(err instanceof Error ? err : new Error(errorMessage));
            };

            wavesurfer.on('ready', handleReady);
            wavesurfer.on('error', handleError);

            return () => {
                wavesurfer.un('ready', handleReady);
                wavesurfer.un('error', handleError);
            };
        }
    }, [wavesurfer, onReady, onError]);

    return (
        <Column spacing="200">

            {/* Waveform container */}
            <div 
                style={{ 
                    position: 'relative',
                    width: '100%',
                    background: '#fafafa',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}
            >
                {/* Grid lines background */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                        pointerEvents: 'none',
                        backgroundImage: `
                            linear-gradient(to right, rgba(33, 150, 243, 0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(33, 150, 243, 0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: '50px 28px',
                    }}
                />
                
                {/* Zero line (horizontal line at center) */}
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: 'rgba(33, 150, 243, 0.4)',
                        zIndex: 1,
                        pointerEvents: 'none',
                        transform: 'translateY(-0.5px)'
                    }}
                />
                
                {/* Amplitude reference lines at ±50% and ±75% */}
                <div
                    style={{
                        position: 'absolute',
                        top: '25%',
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: 'rgba(33, 150, 243, 0.2)',
                        zIndex: 1,
                        pointerEvents: 'none'
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        top: '75%',
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: 'rgba(33, 150, 243, 0.2)',
                        zIndex: 1,
                        pointerEvents: 'none'
                    }}
                />
                
                {/* WaveSurfer container */}
                <div 
                    ref={containerRef} 
                    style={{ 
                        width: '100%',
                        position: 'relative',
                        zIndex: 2,
                        padding: '2px'
                    }} 
                />
            </div>

            {/* Controls and time display */}
            <Row spacing="200">
                <Button
                    type="icon"
                    size='small'
                    onClick={onPlayPause}
                    disabled={!wavesurfer}
                >
                    <Icon tokens={isPlaying ? pauseTokens : playTokens}></Icon>
                </Button>
                <Text type="b200">
                    Time: {formatTime(currentTime)}
                </Text>
            </Row>
        </Column>
    );
};
